import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isEmpty } from 'radash';
import { ApolloLink, useQuery } from '@apollo/client';
import isURL from 'validator/lib/isURL';
import {
  JoinErrorCodeTable,
  MeetingEndedTable,
  openLearningDashboardUrl,
  setLearningDashboardCookie,
} from './service';
import { MeetingEndDataResponse, getMeetingEndData } from './queries';
import useAuthData from '/imports/ui/core/local-states/useAuthData';
import Icon from '/imports/ui/components/common/icon/icon-ts/component';
import Styled from './styles';
import { LoadingContext } from '../common/loading-screen/loading-screen-HOC/component';
import logger from '/imports/startup/client/logger';
import apolloContextHolder from '/imports/ui/core/graphql/apolloContextHolder/apolloContextHolder';
import autoScreenshareService from '/imports/ui/services/auto-screenshare';
// import { STU_LOGOUT_URL } from '../../core/utils/getBaseUrl';
import { courseSelect } from '../app/context';
import Rating from "./rating/component"
import { fetchCourseDetails, logoutUser, sendFeedbackApi, submitMeetGreetFeedbackApi, studentLogouturl, tutorLogouturl } from '../app/service';
import Auth from '/imports/ui/services/auth';
import { notify } from '../../services/notification';
import { toast, ToastContainer } from 'react-toastify';
const intlMessage = defineMessages({
  410: {
    id: 'app.meeting.ended',
    description: 'message when meeting is ended',
  },
  403: {
    id: 'app.error.removed',
    description: 'Message to display when user is removed from the conference',
  },
  430: {
    id: 'app.error.meeting.ended',
    description: 'user logged conference',
  },
  'acl-not-allowed': {
    id: 'app.error.removed',
    description: 'Message to display when user is removed from the conference',
  },
  textarea: {
    id: 'app.feedback.textarea',
    description: 'placeholder for textarea',
  },
  messageEnded: {
    id: 'app.meeting.endedMessage',
    description: 'message saying to go back to home screen',
  },
  messageEndedByUser: {
    id: 'app.meeting.endedByUserMessage',
    description: 'message informing who ended the meeting',
  },
  messageEndedByNoModeratorSingular: {
    id: 'app.meeting.endedByNoModeratorMessageSingular',
    description: 'message informing that the meeting was ended due to no moderator present (singular)',
  },
  messageEndedByNoModeratorPlural: {
    id: 'app.meeting.endedByNoModeratorMessagePlural',
    description: 'message informing that the meeting was ended due to no moderator present (plural)',
  },
  buttonOkay: {
    id: 'app.meeting.endNotification.ok.label',
    description: 'label okay for button',
  },
  confirmDesc: {
    id: 'app.leaveConfirmation.confirmDesc',
    description: 'adds context to confim option',
  },
  [JoinErrorCodeTable.DUPLICATE_USER]: {
    id: 'app.meeting.logout.duplicateUserEjectReason',
    description: 'message for duplicate users',
  },
  [JoinErrorCodeTable.PERMISSION_FAILED]: {
    id: 'app.meeting.logout.permissionEjectReason',
    description: 'message for whom was kicked by doing something without permission',
  },
  [JoinErrorCodeTable.EJECT_USER]: {
    id: 'app.meeting.logout.ejectedFromMeeting',
    description: 'message when the user is removed by someone',
  },
  [JoinErrorCodeTable.SYSTEM_EJECT_USER]: {
    id: 'app.meeting.logout.ejectedFromMeeting',
    description: 'message when the user is removed by the system',
  },
  [JoinErrorCodeTable.MAX_PARTICIPANTS]: {
    id: 'app.meeting.logout.maxParticipantsReached',
    description: 'message when the user is rejected due to max participants limit',
  },
  [JoinErrorCodeTable.VALIDATE_TOKEN]: {
    id: 'app.meeting.logout.validateTokenFailedEjectReason',
    description: 'invalid auth token',
  },
  [JoinErrorCodeTable.USER_INACTIVITY]: {
    id: 'app.meeting.logout.userInactivityEjectReason',
    description: 'message to whom was kicked by inactivity',
  },
  [JoinErrorCodeTable.USER_LOGGED_OUT]: {
    id: 'app.feedback.title',
    description: 'message to whom was kicked by logging out',
  },
  [JoinErrorCodeTable.BANNED_USER_REJOINING]: {
    id: 'app.error.userBanned',
    description: 'message to whom was banned',
  },
  open_activity_report_btn: {
    id: 'app.learning-dashboard.clickHereToOpen',
    description: 'description of link to open activity report',
  },
  [MeetingEndedTable.ENDED_FROM_API]: {
    id: 'app.meeting.endedFromAPI',
    description: '',
  },
  [MeetingEndedTable.ENDED_WHEN_NOT_JOINED]: {
    id: 'app.meeting.endedWhenNoUserJoined',
    description: '',
  },
  [MeetingEndedTable.ENDED_WHEN_LAST_USER_LEFT]: {
    id: 'app.meeting.endedWhenLastUserLeft',
    description: '',
  },
  [MeetingEndedTable.ENDED_AFTER_USER_LOGGED_OUT]: {
    id: 'app.meeting.endedWhenLastUserLeft',
    description: '',
  },
  [MeetingEndedTable.ENDED_AFTER_EXCEEDING_DURATION]: {
    id: 'app.meeting.endedAfterExceedingDuration',
    description: '',
  },
  [MeetingEndedTable.BREAKOUT_ENDED_EXCEEDING_DURATION]: {
    id: 'app.meeting.breakoutEndedAfterExceedingDuration',
    description: '',
  },
  [MeetingEndedTable.BREAKOUT_ENDED_BY_MOD]: {
    id: 'app.meeting.breakoutEndedByModerator',
    description: '',
  },
  [MeetingEndedTable.ENDED_DUE_TO_NO_AUTHED_USER]: {
    id: 'app.meeting.endedDueNoAuthed',
    description: '',
  },
  [MeetingEndedTable.ENDED_DUE_TO_NO_MODERATOR]: {
    id: 'app.meeting.endedDueNoModerators',
    description: '',
  },
  [MeetingEndedTable.ENDED_DUE_TO_SERVICE_INTERRUPTION]: {
    id: 'app.meeting.endedDueServiceInterruption',
    description: '',
  },
});

interface MeetingEndedContainerProps {
  endedBy: string;
  meetingEndedCode: string;
  joinErrorCode: string;
}

interface MeetingEndedProps extends MeetingEndedContainerProps {
  allowDefaultLogoutUrl: boolean;
  skipMeetingEnded: boolean;
  learningDashboardAccessToken: string;
  isModerator: boolean;
  logoutUrl: string;
  learningDashboardBase: string;
  isBreakout: boolean;
}

const MeetingEnded: React.FC<MeetingEndedProps> = ({
  endedBy,
  joinErrorCode,
  meetingEndedCode,
  allowDefaultLogoutUrl,
  skipMeetingEnded,
  learningDashboardAccessToken,
  isModerator,
  logoutUrl,
  learningDashboardBase,
  isBreakout,
}) => {
  const loadingContextInfo = useContext(LoadingContext);
  const intl = useIntl();
  const [{
    authToken,
    meetingId,
  }] = useAuthData();

  const generateEndMessage = useCallback((joinErrorCode: string, meetingEndedCode: string, endedBy: string) => {
    if (!isEmpty(endedBy)) {
      // return intl.formatMessage(intlMessage.messageEndedByUser, { 0: '' });
      return 'This lesson has ended'
    }
    // OR opetaror always returns the first truthy value

    const code = meetingEndedCode || joinErrorCode || '410';
    return intl.formatMessage(intlMessage[code]);
  }, []);
  const [selectedStar, setSelectedStar] = useState<number>(0);
  const [feedbackComment, setFeedbackComment] = useState<string>('');
  const [courseDetails, setCourseDetails] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false);

  // Meet & Greet specific feedback fields
  const [communication, setCommunication] = useState<string>('');
  const [understandsNeeds, setUnderstandsNeeds] = useState<string>('');
  const [proceedDecision, setProceedDecision] = useState<string>('');

  // Check if this is a Meet & Greet session
  const isMeetGreet = courseDetails?.course?.isMeetGreet ?? false;
  const meetGreetId = courseDetails?.course?.meetGreetId;

  useEffect(() => {
    if (!Auth?._meetingID && !Auth?._externUserID) return;
    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID
    }

    // FETCH COURSE DETAILS FROM API
    fetchCourseDetails(apiPayload)
      .then((res: any) => {
        setCourseDetails({ course: res.data.data, });
      })

  }, [Auth?._meetingID, Auth?._externUserID])


  const confirmRedirect = (isBreakout: boolean, allowRedirect: boolean) => {
    if (isBreakout) window.close();
    if (allowRedirect) {
      let finalLogoutUrl = tutorLogouturl() ?? logoutUrl;
      // if not moderator then change logoutUrl
      if (!isModerator) {
        finalLogoutUrl = studentLogouturl() ?? 'https://portal.lessonwise.org/student/dashboard';
      }

      if (isURL(finalLogoutUrl)) {
        const reason = generateEndMessage(joinErrorCode, meetingEndedCode, endedBy);
        const finalUrl = reason
          ? `${finalLogoutUrl}${finalLogoutUrl.includes('?') ? '&' : '?'}reason=${encodeURIComponent(reason)}`
          : finalLogoutUrl;
        window.location.href = finalUrl;
      }
    }
  };
  

  // const sendFeedback = (isBreakout: boolean, allowRedirect: boolean) => {
  //   // if not moderator then change logoutUrl
  //   if (!isModerator) {
  //     setLoading(true)
  //     const payload = {
  //       // userName: Auth?.fullname,
  //       internalMeetingID: Auth?._meetingID,
  //       courseId: courseDetails?.course?.courseDetail?.courseId,
  //       courseSessionId: courseDetails?.course?.courseDetail?.courseSessionId,
  //       userId: Auth._externUserID,
  //       rating: selectedStar ?? 0,
  //       comments: feedbackComment ?? '',
  //       // userRole: localUserRole, // set according to your context
  //     };

  //     // replace with your student logout URL
  //     if (selectedStar > 0) {
  //       // call api here
  //       sendFeedbackApi(payload).then(() => {

  //       }).catch((err) => {
  //         // console.error(err?.response?.data || err?.message || err);
  //         toast.error(err?.message ?? 'something went wrong!');
  //         console.log(err?.message)
  //       }).finally(() => {
  //         setTimeout(() => {
  //           // setLoading(false); // 👈 delay reset
  //           confirmRedirect(isBreakout, allowRedirect);
  //         }, 5000); // 1 second delay
  //       });
  //     }
  //   } else {
  //     confirmRedirect(isBreakout, allowRedirect)
  //   }
  // }




  // const sendFeedback = (isBreakout: boolean, allowRedirect: boolean) => {
  //   // if not moderator then change logoutUrl
  //   if (!isModerator) {
  //     setLoading(true)
  //     const payload = {
  //       // userName: Auth?.fullname,
  //       internalMeetingID: Auth?._meetingID,
  //       courseId: courseDetails?.course?.courseDetail?.courseId,
  //       courseSessionId: courseDetails?.course?.courseDetail?.courseSessionId,
  //       userId: Auth._externUserID,
  //       rating: selectedStar ?? 0,
  //       comments: feedbackComment ?? '',
  //       // userRole: localUserRole, // set according to your context
  //     };

  //     // replace with your student logout URL
  //     if (selectedStar > 0) {
  //       // call api here
  //       sendFeedbackApi(payload).then(() => {

  //       }).catch((err) => {
  //         // console.error(err?.response?.data || err?.message || err);
  //         toast.error(err?.message ?? 'something went wrong!');
  //         console.log(err?.message)
  //       }).finally(() => {
  //         setTimeout(() => {
  //           // setLoading(false); // 👈 delay reset
  //           confirmRedirect(isBreakout, allowRedirect);
  //         }, 5000); // 1 second delay
  //       });
  //     }
  //   } else {
  //     confirmRedirect(isBreakout, allowRedirect)
  //   }
  // }



  useEffect(() => {
    if (!Auth?._meetingID && !Auth?._externUserID) {
      confirmRedirect(false, true)
      return
    };

    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID
    }

    // FETCH COURSE DETAILS FROM API
    fetchCourseDetails(apiPayload)
      .then((res: any) => {
        setCourseDetails({ course: res.data.data, });
      })

  }, [])

  const sendFeedback = (isBreakout: boolean, allowRedirect: boolean) => {
    // if not moderator then change logoutUrl
    if (!isModerator) {
      setLoading(true)

      const courseSessionId = localStorage.getItem('courseSessionID');

      const apiPayload = {
        internalMeetingID: Auth?._meetingID,
        externalUserID: Auth?._externUserID,
        courseSessionId: courseSessionId ?? courseDetails?.course?.courseDetail?.courseSessionId,
        isPrepareLesson: courseDetails?.course?.courseDetail?.isPrepareLesson ?? false
      }

      // Helper to logout and redirect
      const doLogoutAndRedirect = (redirectUrl?: string) => {
        logoutUser(apiPayload)
          .catch((error) => {
            logger.error({ logCode: 'logout_user_failed', extraInfo: { error: error?.message } }, 'Logout user failed')
          })
          .finally(() => {
            localStorage.removeItem('courseSessionID');
            if (redirectUrl) {
              window.location.href = redirectUrl;
            } else {
              confirmRedirect(isBreakout, allowRedirect);
            }
          });
      };

      // Check if this is a Meet & Greet session
      if (isMeetGreet && meetGreetId) {
        // Helper to logout and show completion state for Meet & Greet
        const doLogoutAndShowComplete = () => {
          logoutUser(apiPayload)
            .catch((error) => {
              logger.error({ logCode: 'logout_user_failed', extraInfo: { error: error?.message } }, 'Logout user failed')
            })
            .finally(() => {
              localStorage.removeItem('courseSessionID');
              // Try to close the tab (only works if opened via JS)
              window.close();
              // If still here after 500ms, redirect to a blank thank you state
              // The window.close() didn't work, so show completion message
            });
        };

        // Submit Meet & Greet feedback
        const meetGreetPayload = {
          internalMeetingID: Auth?._meetingID,
          meetGreetId: meetGreetId,
          confidenceRating: selectedStar,
          communication: communication || undefined,
          understandsNeeds: understandsNeeds || undefined,
          proceedDecision: proceedDecision || undefined,
          additionalComments: feedbackComment || undefined,
        };

        submitMeetGreetFeedbackApi(meetGreetPayload)
          .then(() => {
            // Feedback submitted successfully - show thank you page
            // Replace the entire page content with a simple thank you message
            document.body.style.cssText = 'margin: 0; padding: 0; width: 100%; height: 100%;';
            document.documentElement.style.cssText = 'margin: 0; padding: 0; width: 100%; height: 100%;';
            document.body.innerHTML = `
              <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; background: #EEF0F2; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                <div style="background: white; padding: 48px; border-radius: 12px; text-align: center; max-width: 400px; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
                  <div style="width: 64px; height: 64px; background: #e6f9f0; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px;">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00ce77" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <h1 style="color: #00265e; font-size: 24px; font-weight: 700; margin: 0 0 12px;">Thank You!</h1>
                  <p style="color: #4b5563; font-size: 16px; margin: 0 0 8px;">Your feedback has been submitted.</p>
                  <p style="color: #9ca3af; font-size: 14px; margin: 0;">You can close this tab now.</p>
                </div>
              </div>
            `;
            doLogoutAndShowComplete();
          })
          .catch((err) => {
            toast.error(err?.message ?? 'Something went wrong!');
            // Still logout even if feedback fails
            setTimeout(() => {
              doLogoutAndShowComplete();
            }, 2000);
          });
      } else {
        // Regular lesson feedback - logout first (existing behavior)
        logoutUser(apiPayload).catch((error) => {
          logger.error({ logCode: 'logout_user_failed', extraInfo: { error: error?.message } }, 'Logout user failed')
        });

        const payload = {
          internalMeetingID: Auth?._meetingID,
          courseId: courseDetails?.course?.courseDetail?.courseId,
          courseSessionId: courseSessionId ?? courseDetails?.course?.courseDetail?.courseSessionId,
          userId: Auth._externUserID,
          rating: selectedStar ?? 0,
          comments: feedbackComment ?? '',
        };

        if (selectedStar > 0) {
          sendFeedbackApi(payload).then(() => {
          }).catch((err) => {
            toast.error(err?.message ?? 'something went wrong!');
          }).finally(() => {
            setTimeout(() => {
              confirmRedirect(isBreakout, allowRedirect);
              localStorage.removeItem('courseSessionID');
            }, 5000);
          });
        } else {
          confirmRedirect(isBreakout, allowRedirect)
          localStorage.removeItem('courseSessionID');
        }
      }
    } else {
      setLoading(false)
      confirmRedirect(isBreakout, allowRedirect)
      localStorage.removeItem('courseSessionID');
    }
  }

  const logoutButton = useMemo(() => {
    const { locale } = intl;

    // Meet & Greet feedback form
    if (isMeetGreet && !isModerator) {
      return (
        <Styled.FeedbackForm>
          <Styled.FeedbackHeader>
            <Styled.FeedbackTitle>
              How was your session?
            </Styled.FeedbackTitle>
            <Styled.FeedbackIntro>
              We'd love to hear about your Meet & Greet experience
            </Styled.FeedbackIntro>
          </Styled.FeedbackHeader>

          {/* Question 1: Star Rating */}
          <Styled.QuestionGroup>
            <Styled.QuestionLabel>
              How confident do you feel about working with this tutor?
            </Styled.QuestionLabel>
            <Styled.RatingHint>
              <span>1 – Not confident</span>
              <span>5 – Very confident</span>
            </Styled.RatingHint>
            <div data-test="rating">
              <Rating
                intl
                total={5}
                hideLabel
                onRate={(star: number) => { setSelectedStar(star) }}
              />
            </div>
          </Styled.QuestionGroup>

          {/* Question 2: Communication */}
          <Styled.QuestionGroup>
            <Styled.QuestionLabel>
              Did the conversation feel comfortable and easy?
            </Styled.QuestionLabel>
            <Styled.SelectWrapper>
              <Styled.Select
                value={communication}
                onChange={(e) => setCommunication(e.target.value)}
              >
                <option value="">Select an option...</option>
                <option value="VERY_MUCH">Yes, very much</option>
                <option value="MOSTLY">Mostly</option>
                <option value="NOT_REALLY">Not quite</option>
              </Styled.Select>
            </Styled.SelectWrapper>
          </Styled.QuestionGroup>

          {/* Question 3: Understanding */}
          <Styled.QuestionGroup>
            <Styled.QuestionLabel>
              Do you feel learning needs and goals were understood?
            </Styled.QuestionLabel>
            <Styled.SelectWrapper>
              <Styled.Select
                value={understandsNeeds}
                onChange={(e) => setUnderstandsNeeds(e.target.value)}
              >
                <option value="">Select an option...</option>
                <option value="YES">Yes</option>
                <option value="MOSTLY">Mostly</option>
                <option value="NOT_YET">Not quite</option>
              </Styled.Select>
            </Styled.SelectWrapper>
          </Styled.QuestionGroup>

          {/* Question 4: Proceed Decision */}
          <Styled.QuestionGroup>
            <Styled.QuestionLabel>
              Based on this session, what would you like to do next?
            </Styled.QuestionLabel>
            <Styled.SelectWrapper>
              <Styled.Select
                value={proceedDecision}
                onChange={(e) => setProceedDecision(e.target.value)}
              >
                <option value="">Select an option...</option>
                <option value="YES">Happy to proceed with this tutor</option>
                <option value="EXPLORE_DIFFERENT">I'd like to explore a different tutor</option>
              </Styled.Select>
            </Styled.SelectWrapper>
          </Styled.QuestionGroup>

          {/* Question 5: Additional Comments */}
          <Styled.QuestionGroup>
            <Styled.QuestionLabel>
              Any additional comments? (optional)
            </Styled.QuestionLabel>
            <Styled.FeedbackTextArea
              rows={2}
              id="feedbackComment"
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Share any other thoughts..."
              aria-describedby="textareaDesc"
            />
          </Styled.QuestionGroup>

          <Styled.SubmitButton
            onClick={() => {
              // Validate required fields
              if (selectedStar === 0) {
                toast.error('Please rate your confidence level (1-5 stars)');
                return;
              }
              sendFeedback(isBreakout, allowDefaultLogoutUrl);
            }}
            disabled={loading}
            aria-details={intl.formatMessage(intlMessage.confirmDesc)}
          >
            Submit Feedback
          </Styled.SubmitButton>
          <ToastContainer
            autoClose={5000}
            toastClassName="toastClass"
            progressClassName="toastProgressClass"
            newestOnTop={false}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            icon={false}
            theme="colored"
          />
        </Styled.FeedbackForm>
      );
    }

    // Regular lesson feedback form
    return (
      (
        <Styled.Wrapper>
          <Styled.Text>
            {
              !isModerator ? `We'd love to hear about your experience today with ${courseDetails?.course?.courseDetail?.userId?.username} (optional)` :
                'You will be forwarded back to the home screen'
            }
          </Styled.Text>

          {
            !isModerator &&
            <>
              <div data-test="rating">
                <Rating
                  intl
                  total={5}
                  onRate={(star: number) => { setSelectedStar(star) }}
                />
              </div>
              {selectedStar > 0 && (
                <Styled.TextArea
                  rows={5}
                  id="feedbackComment"
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  placeholder={'How can we make Lessonwise better?'}
                  aria-describedby="textareaDesc"
                />
              )}
            </>
          }


          <Styled.MeetingEndedButton
            color="primary"
            onClick={() => sendFeedback(isBreakout, allowDefaultLogoutUrl)}
            disabled={loading}
            aria-details={intl.formatMessage(intlMessage.confirmDesc)}
          >
            {selectedStar > 0 ? 'Send Feedback' : intl.formatMessage(intlMessage.buttonOkay)}
          </Styled.MeetingEndedButton>
          <ToastContainer
            autoClose={5000}
            toastClassName="toastClass"
            progressClassName="toastProgressClass"
            newestOnTop={false}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            icon={false}
            theme="colored"
          />
        </Styled.Wrapper>
      )
    );
  }, [learningDashboardAccessToken, isModerator, meetingId, authToken, learningDashboardBase, selectedStar, loading, feedbackComment, isMeetGreet, communication, understandsNeeds, proceedDecision]);

  useEffect(() => {
    // Sets Loading to falsed and removes loading splash screen
    loadingContextInfo.setLoading(false);
    // Stops all media tracks
    window.dispatchEvent(new Event('StopAudioTracks'));
    // get the media tag from the session storage
    // @ts-ignore
    const data = window.meetingClientSettings.public.media;
    // get media element and stops it and removes the audio source
    const mediaElement = document.querySelector<HTMLMediaElement>(data.mediaTag);
    if (mediaElement) {
      mediaElement.pause();
      mediaElement.srcObject = null;
    }
    // stops apollo client and removes it connection
    const apolloClient = apolloContextHolder.getClient();
    // stops client queries
    if (apolloClient) {
      apolloClient.stop();
    }

    apolloContextHolder.setShouldRetry(false);

    const ws = apolloContextHolder.getLink();
    // stops client connection after 5 seconds, if made immediately some data is lost
    if (ws) {
      setTimeout(() => {
        // overwrites the link with an empty link
        // if not new connection is made
        apolloClient.setLink(ApolloLink.empty());
        // closes the connection
        ws.terminate();
      }, 5000);
    }

    // Reset auto-screenshare service state
    autoScreenshareService.reset();
  }, []);

  if (skipMeetingEnded) {
    confirmRedirect(isBreakout, allowDefaultLogoutUrl);
    return <></>; // even though well redirect, return empty component and prevent lint error
  }

  return (
    <Styled.Parent>
      <Styled.Modal data-test="meetingEndedModal">
        <Styled.Content>
          {/* Hide the title for Meet & Greet sessions */}
          {!isMeetGreet && (
            <Styled.Title>
              {generateEndMessage(joinErrorCode, meetingEndedCode, endedBy)}
            </Styled.Title>
          )}
          {allowDefaultLogoutUrl ? logoutButton : null}
        </Styled.Content>
      </Styled.Modal>
    </Styled.Parent>
  );
};

const MeetingEndedContainer: React.FC<MeetingEndedContainerProps> = ({
  endedBy,
  meetingEndedCode,
  joinErrorCode,
}) => {
  const {
    loading: meetingEndLoading,
    error: meetingEndError,
    data: meetingEndData,
  } = useQuery<MeetingEndDataResponse>(getMeetingEndData);


  // Check if the meeting ended due to "Save & Exit" action (classroom preparation completion)
  const isSaveExitAction = localStorage.getItem('isPrepareLesson') === 'true' ? true : false;

  // Only skip meeting ended modal if in classroom preparation mode AND it's a Save & Exit action
  const shouldSkipMeetingEnded = isSaveExitAction;

  if (meetingEndLoading || !meetingEndData) {
    return (
      <MeetingEnded
        endedBy=""
        joinErrorCode=""
        meetingEndedCode=""
        allowDefaultLogoutUrl={false}
        skipMeetingEnded={shouldSkipMeetingEnded}
        learningDashboardAccessToken=""
        isModerator={false}
        logoutUrl=""
        learningDashboardBase=""
        isBreakout={false}
      />
    );
  }

  if (meetingEndError) {
    logger.error('Error on fetching meeting end data: ', meetingEndError);
    return (
      <MeetingEnded
        endedBy=""
        joinErrorCode=""
        meetingEndedCode=""
        allowDefaultLogoutUrl={false}
        skipMeetingEnded={shouldSkipMeetingEnded}
        learningDashboardAccessToken=""
        isModerator={false}
        logoutUrl=""
        learningDashboardBase=""
        isBreakout={false}
      />
    );
  }

  const {
    user_current,
  } = meetingEndData;
  const {
    isModerator,
    logoutUrl,
    meeting,
  } = user_current[0];

  const {
    learningDashboard,
    isBreakout,
    clientSettings,
  } = meeting;

  const {
    allowDefaultLogoutUrl,
    learningDashboardBase,
  } = clientSettings;

  // Override skipMeetingEnded to true if in classroom preparation mode
  const finalSkipMeetingEnded = shouldSkipMeetingEnded;

  return (
    <MeetingEnded
      endedBy={endedBy}
      joinErrorCode={joinErrorCode}
      meetingEndedCode={meetingEndedCode}
      allowDefaultLogoutUrl={allowDefaultLogoutUrl}
      skipMeetingEnded={finalSkipMeetingEnded}
      learningDashboardAccessToken={learningDashboard?.learningDashboardAccessToken}
      isModerator={isModerator}
      logoutUrl={!isModerator ? studentLogouturl() : tutorLogouturl()}
      learningDashboardBase={learningDashboardBase}
      isBreakout={isBreakout}
    />
  );
};

export default MeetingEndedContainer;
